/**
 * The onboarding card at the reception desk: name, role and avatar in three steps, drawn on
 * one canvas texture with a step indicator, real text fields and clear buttons. Replaces the
 * three-mesh-ui panels, whose flex layout ran titles and labels into each other.
 */
import { ROLES, ROLE_STYLES, type Role } from '@clinspace/shared';
import { BUILT_IN_AVATARS, type AvatarId } from '../../app/profile.ts';
import { CanvasPanel, type Region } from '../../dashboards/CanvasPanel.ts';
import {
  PALETTE,
  chip,
  fillRoundRect,
  fitSize,
  font,
  glassPanel,
  text,
} from '../../dashboards/charts.ts';
import type { TextField } from '../../queries/textField.ts';
import { drawTextField } from '../../queries/textField.ts';
import type { InteractiveSystem } from '../../ui/Interactive.ts';

export type OnboardingStep = 'name' | 'role' | 'avatar';

export interface OnboardingState {
  step: OnboardingStep;
  name: TextField;
  vrm: TextField;
  focused: 'name' | 'vrm';
  nameError: string;
  vrmStatus: string;
  role: Role;
  avatar: AvatarId | undefined;
}

export interface OnboardingActions {
  submitName(): void;
  chooseRole(role: Role): void;
  chooseAvatar(id: AvatarId): void;
  loadVrm(): void;
  finish(): void;
  back(): void;
  next(): void;
  focus(field: 'name' | 'vrm'): void;
}

type Action =
  | { kind: 'role'; role: Role }
  | { kind: 'avatar'; id: AvatarId }
  | { kind: 'button'; action: 'continue' | 'back' | 'loadVrm' | 'finish' }
  | { kind: 'field'; field: 'name' | 'vrm' };

/** One line about each role, shown under its name. */
export const ROLE_BLURBS: Record<Role, string> = {
  'Clinical Data Manager': 'Data cleaning, queries, database lock',
  'Medical Monitor': 'Safety, eligibility, medical review',
  'CRA / Site Monitor': 'Site visits, SDV, site follow-up',
  Biostatistician: 'Analysis sets, data quality, outputs',
  'Safety Physician': 'SAEs, causality, signal review',
  'Principal Investigator': 'Site conduct, subject care, sign-off',
  'Sponsor Observer': 'View only: cannot raise or close queries',
};

const W_M = 1.3;
const H_M = 0.82;
const PX_W = 1040;
const PX_H = 656;

export class OnboardingPanel {
  readonly panel: CanvasPanel;

  constructor(
    interactive: InteractiveSystem,
    private readonly state: () => OnboardingState,
    private readonly actions: OnboardingActions,
    private readonly sounds: { click(): void },
  ) {
    this.panel = new CanvasPanel({
      width: W_M,
      height: H_M,
      pixelWidth: PX_W,
      pixelHeight: PX_H,
      name: 'onboarding-card',
      resolution: 1.75,
      glass: true,
    });
    this.panel.setDraw((ctx, panel) => this.draw(ctx, panel));
    this.panel.onSelect = (region) => this.select(region);
    this.panel.attach(interactive);
  }

  get mesh() {
    return this.panel.mesh;
  }

  invalidate(): void {
    this.panel.invalidate();
  }

  update(): void {
    this.panel.update();
  }

  private draw(ctx: CanvasRenderingContext2D, panel: CanvasPanel): void {
    const s = this.state();
    ctx.clearRect(0, 0, PX_W, PX_H);
    glassPanel(ctx, { x: 2, y: 2, w: PX_W - 4, h: PX_H - 4 }, 26);
    this.drawSteps(ctx, s.step);
    if (s.step === 'name') this.drawName(ctx, panel, s);
    else if (s.step === 'role') this.drawRole(ctx, panel, s);
    else this.drawAvatar(ctx, panel, s);
  }

  /** "1 Your name › 2 Your role › 3 Your avatar", current step highlighted. */
  private drawSteps(ctx: CanvasRenderingContext2D, step: OnboardingStep): void {
    const steps: [OnboardingStep, string][] = [
      ['name', 'Your name'],
      ['role', 'Your role'],
      ['avatar', 'Your avatar'],
    ];
    const current = steps.findIndex(([k]) => k === step);
    let x = 36;
    steps.forEach(([, label], i) => {
      const done = i < current;
      const now = i === current;
      ctx.beginPath();
      ctx.arc(x + 15, 40, 15, 0, Math.PI * 2);
      ctx.fillStyle = now ? PALETTE.teal : done ? PALETTE.tealDim : '#253242';
      ctx.fill();
      text(ctx, String(i + 1), x + 15, 46, {
        size: 15,
        weight: 700,
        align: 'center',
        colour: now || done ? '#04141c' : PALETTE.muted,
      });
      text(ctx, label, x + 38, 47, {
        size: 17,
        weight: now ? 700 : 500,
        colour: now ? PALETTE.text : PALETTE.muted,
      });
      ctx.font = font(17, now ? 700 : 500);
      x += 38 + ctx.measureText(label).width + 22;
      if (i < steps.length - 1) {
        ctx.fillStyle = '#253242';
        ctx.fillRect(x - 12, 37, 18, 2);
        x += 14;
      }
    });
  }

  private button(
    ctx: CanvasRenderingContext2D,
    panel: CanvasPanel,
    label: string,
    action: 'continue' | 'back' | 'loadVrm' | 'finish',
    x: number,
    opts: { primary?: boolean; alignRight?: boolean } = {},
  ): number {
    ctx.font = font(20, 600);
    const w = ctx.measureText(label).width + 56;
    const left = opts.alignRight ? x - w : x;
    const r = chip(ctx, label, left, PX_H - 84, {
      active: opts.primary,
      hovered: panel.isHovered(action),
      h: 56,
      size: 20,
      minW: w,
      colour: opts.primary ? PALETTE.teal : undefined,
    });
    panel.region<Action>({ id: action, ...r, data: { kind: 'button', action } });
    return opts.alignRight ? left : left + r.w;
  }

  private drawName(ctx: CanvasRenderingContext2D, panel: CanvasPanel, s: OnboardingState): void {
    text(ctx, 'Welcome to ClinSpaceXR', 36, 132, { size: 34, weight: 600, display: true });
    text(ctx, 'Your name appears on your name tag and on the queries you raise.', 36, 172, {
      size: 19,
      colour: PALETTE.muted,
      maxWidth: PX_W - 72,
    });
    const field = { x: 36, y: 206, w: PX_W - 72, h: 86 };
    drawTextField(ctx, field, s.name, {
      placeholder: 'Type your name',
      active: s.focused === 'name',
      size: 30,
    });
    panel.region<Action>({
      id: 'field:name',
      ...field,
      data: { kind: 'field', field: 'name' },
      selectable: false,
    });
    text(
      ctx,
      s.nameError ||
        '2 to 24 characters · type with the keyboard below, or your own keyboard on desktop',
      36,
      324,
      {
        size: 17,
        colour: s.nameError ? '#ff9aa8' : PALETTE.muted,
        maxWidth: PX_W - 72,
      },
    );
    this.button(ctx, panel, 'Continue', 'continue', PX_W - 36, { primary: true, alignRight: true });
  }

  private drawRole(ctx: CanvasRenderingContext2D, panel: CanvasPanel, s: OnboardingState): void {
    const name = s.name.trimmed || 'there';
    text(ctx, `Welcome, ${name}. What is your role?`, 36, 128, {
      size: 30,
      display: true,
      weight: 700,
      maxWidth: PX_W - 72,
    });
    text(
      ctx,
      'Your role colours your name tag. Observers can view but not act on queries.',
      36,
      164,
      {
        size: 18,
        colour: PALETTE.muted,
        maxWidth: PX_W - 72,
      },
    );
    const top = 188;
    const cols = 2;
    const gap = 14;
    const cw = (PX_W - 72 - gap) / cols;
    const ch = 84;
    ROLES.forEach((role, i) => {
      const r = {
        x: 36 + (i % cols) * (cw + gap),
        y: top + Math.floor(i / cols) * (ch + 8),
        w: cw,
        h: ch,
      };
      const id = `role:${i}`;
      const active = s.role === role;
      fillRoundRect(
        ctx,
        r,
        12,
        active ? 'rgba(63,214,255,0.22)' : panel.isHovered(id) ? '#26354a' : '#1a2433',
      );
      if (active) {
        ctx.strokeStyle = PALETTE.teal;
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.roundRect(r.x + 1.5, r.y + 1.5, r.w - 3, r.h - 3, 12);
        ctx.stroke();
      }
      const style = ROLE_STYLES[role];
      ctx.beginPath();
      ctx.arc(r.x + 34, r.y + ch / 2, 20, 0, Math.PI * 2);
      ctx.fillStyle = style.hex;
      ctx.fill();
      text(ctx, style.short, r.x + 34, r.y + ch / 2 + 5, {
        size: 11,
        weight: 700,
        align: 'center',
        colour: '#ffffff',
      });
      const tw = r.w - 76;
      text(ctx, role, r.x + 66, r.y + 36, {
        size: fitSize(ctx, role, tw, 21, 16, 700),
        weight: 700,
        maxWidth: tw,
      });
      text(ctx, ROLE_BLURBS[role], r.x + 66, r.y + 64, {
        size: fitSize(ctx, ROLE_BLURBS[role], tw, 16, 12),
        colour: PALETTE.muted,
        maxWidth: tw,
      });
      panel.region<Action>({ id, ...r, data: { kind: 'role', role } });
    });
    this.button(ctx, panel, 'Back', 'back', 36);
    this.button(ctx, panel, 'Continue', 'continue', PX_W - 36, { primary: true, alignRight: true });
  }

  private drawAvatar(ctx: CanvasRenderingContext2D, panel: CanvasPanel, s: OnboardingState): void {
    text(ctx, 'Choose your avatar', 36, 128, { size: 30, weight: 600, display: true });
    text(ctx, 'Others see you as this person. It turns on the stand to your right.', 36, 162, {
      size: 18,
      colour: PALETTE.muted,
    });
    const top = 184;
    const cols = 3;
    const gap = 12;
    const cw = (PX_W - 72 - gap * (cols - 1)) / cols;
    const ch = 96;
    BUILT_IN_AVATARS.forEach((a, i) => {
      const r = {
        x: 36 + (i % cols) * (cw + gap),
        y: top + Math.floor(i / cols) * (ch + 10),
        w: cw,
        h: ch,
      };
      const id = `avatar:${i}`;
      const active = s.avatar === a.id;
      fillRoundRect(
        ctx,
        r,
        12,
        active ? 'rgba(63,214,255,0.22)' : panel.isHovered(id) ? '#26354a' : '#1a2433',
      );
      if (active) {
        ctx.strokeStyle = PALETTE.teal;
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.roundRect(r.x + 1.5, r.y + 1.5, r.w - 3, r.h - 3, 12);
        ctx.stroke();
      }
      text(ctx, a.name, r.x + 18, r.y + 40, { size: 23, weight: 700 });
      text(ctx, a.look, r.x + 18, r.y + 72, {
        size: 16,
        colour: PALETTE.muted,
        maxWidth: r.w - 30,
      });
      panel.region<Action>({ id, ...r, data: { kind: 'avatar', id: a.id } });
    });
    const fieldTop = top + 2 * (ch + 10) + 8;
    text(ctx, 'Or load a VRM avatar by URL (optional)', 36, fieldTop + 14, {
      size: 17,
      colour: PALETTE.muted,
    });
    const field = { x: 36, y: fieldTop + 24, w: PX_W - 72, h: 58 };
    drawTextField(ctx, field, s.vrm, {
      placeholder: 'https://example.org/avatar.vrm',
      active: s.focused === 'vrm',
      size: 20,
    });
    panel.region<Action>({
      id: 'field:vrm',
      ...field,
      data: { kind: 'field', field: 'vrm' },
      selectable: false,
    });
    if (s.vrmStatus)
      text(ctx, s.vrmStatus, 36, field.y + field.h + 22, {
        size: 16,
        colour: '#ff9aa8',
        maxWidth: PX_W - 72,
      });
    let x = this.button(ctx, panel, 'Back', 'back', 36);
    x = this.button(ctx, panel, 'Load VRM', 'loadVrm', x + 12);
    void x;
    this.button(ctx, panel, 'Enter the institute', 'finish', PX_W - 36, {
      primary: true,
      alignRight: true,
    });
  }

  private select(region: Region): void {
    const a = region.data as Action | undefined;
    if (!a) return;
    this.sounds.click();
    switch (a.kind) {
      case 'role':
        this.actions.chooseRole(a.role);
        break;
      case 'avatar':
        this.actions.chooseAvatar(a.id);
        break;
      case 'field':
        this.actions.focus(a.field);
        break;
      case 'button':
        if (a.action === 'continue') this.actions.next();
        else if (a.action === 'back') this.actions.back();
        else if (a.action === 'loadVrm') this.actions.loadVrm();
        else this.actions.finish();
        break;
    }
    this.panel.invalidate();
  }

  dispose(interactive: InteractiveSystem): void {
    this.panel.detach(interactive);
    this.panel.dispose();
  }
}
